import React, { useState } from 'react';
import { Form, Select, DatePicker, Upload, Button, Space, message } from 'antd';
import { Staff } from '../../services/staffs';
import { UploadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import api from '../../services/api';
import customerApi from '../../services/customerApi';
import { CustomerRequest, CustomerReportingRequest } from '../../services/customers';

interface User {
  id: number;
  name: string;
  email: string;
}

interface EngagementTabProps {
  form: any;
  staff: User[];
  teams?: any[];
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  team?: { id: number; name: string } | null;
  reportingFiles?: {
    tax?: UploadFile[];
  };
  onFileChange?: (field: string, files: UploadFile[]) => void;
  customerId?: number;
  onEngagementDateChange?: () => void;
  latestRequests?: {
    customer?: CustomerRequest | null;
    reporting?: CustomerReportingRequest | null;
  };
}

interface EngagementRow {
  key: string;
  label: string;
  staffField: string;
  appointDateField: string;
  resignDateField: string;
  attachmentField?: string;
  hasAttachment: boolean;
}

export const EngagementTab: React.FC<EngagementTabProps> = ({
  form,
  staff,
  teams = [],
  isEditing = false,
  isCustomerPortal = false,
  team = null,
  reportingFiles = {},
  onFileChange,
  customerId,
  onEngagementDateChange,
  latestRequests = {},
}) => {
  const canEdit = isEditing && !isCustomerPortal;
  const [fileActionLoading, setFileActionLoading] = useState<Record<string, { view?: boolean }>>({});
  const requestedCustomer = latestRequests.customer;
  const requestedReporting = latestRequests.reporting;

  const getRequestedValue = (fieldName: string): any => {
    if (!requestedCustomer) return null;
    return (requestedCustomer as any)[fieldName] ?? null;
  };

  const normalizeRequestedDate = (value: any): string => {
    if (!value) return '';
    if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
    if (typeof value === 'string') return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    return '';
  };

  const renderRequestedText = (value: string | null) => {
    if (!value) return null;
    return (
      <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
        Requested: {value}
      </div>
    );
  };

  const setFileLoading = (field: string, action: 'view', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [action]: isLoading,
      },
    }));
  };

  const handleView = async (field: string, file: UploadFile) => {
    if (!file || !customerId) return;
    
    try {
      setFileLoading(field, 'view', true);
      // If it's a new file (has originFileObj), use it directly
      if (file.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const newWindow = window.open(blobUrl, '_blank');

        if (newWindow) {
          newWindow.addEventListener('beforeunload', () => {
            URL.revokeObjectURL(blobUrl);
          });
        }
        setTimeout(() => {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }, 60000); // 60 seconds
      } else {
        // For existing files from S3, fetch via API
        const isCustomerPortalPath = window.location.pathname === '/customer'
          || window.location.pathname.startsWith('/customer/');
        let url: string;
        let apiClient: any;
        
        if (isCustomerPortalPath) {
          url = `/customer/supporting-files/${customerId}/${field}/view`;
          apiClient = customerApi;
        } else {
          url = `/customers/${customerId}/supporting-files/${field}/view`;
          apiClient = api;
        }
        
        const response = await apiClient.get(url, {
          responseType: 'blob',
        });
        
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'application/octet-stream' 
        });
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (error: any) {
      message.error('Failed to view file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const handleViewRequested = async (field: string) => {
    if (!customerId) return;
    try {
      setFileLoading(field, 'view', true);
      const url = `/customer-requests/${customerId}/reporting/${field}/view`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      message.error('Failed to view requested file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const renderAttachmentField = (field: string, files?: UploadFile[]) => {
    if (canEdit) {
      const hasFile = !!files?.length;
      return (
        <Space size="small">
          <Upload
            fileList={files || []}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => {
              if (onFileChange) {
                onFileChange(field, info.fileList);
              }
            }}
            maxCount={1}
            accept=".update,.png,.jpg,.jpeg"
          >
            <Button size="small" icon={<UploadOutlined />} style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }} />
          </Upload>
          {hasFile && (
            <>
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={fileActionLoading[field]?.view}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  const file = files?.[0];
                  if (file && customerId) {
                    handleView(field, file);
                  }
                }}
                />
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  if (onFileChange) {
                    onFileChange(field, []);
                  }
                  }}
                />
            </>
          )}
          {field === 'tax' && requestedReporting?.tax && (
            <Button
              size="small"
              icon={<EyeOutlined style={{ color: '#faad14' }} />}
              style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
              onClick={() => {
                handleViewRequested(field);
              }}
            />
          )}
        </Space>
      );
    } else {
      return (
        <div>
          {files && files.length > 0 ? (
            <Space size="small">
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={fileActionLoading[field]?.view}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  const file = files[0];
                  if (file && customerId) {
                    handleView(field, file);
                  }
                }}
              />
              {field === 'tax' && requestedReporting?.tax && (
                <Button
                  size="small"
                  icon={<EyeOutlined style={{ color: '#faad14' }} />}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => {
                    handleViewRequested(field);
                  }}
                />
              )}
            </Space>
          ) : (
            <Space size="small">
              <div style={{ color: '#999', fontSize: '12px' }}>No attachment</div>
              {field === 'tax' && requestedReporting?.tax && (
                <Button
                  size="small"
                  icon={<EyeOutlined style={{ color: '#faad14' }} />}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => {
                    handleViewRequested(field);
                  }}
                />
              )}
            </Space>
          )}
        </div>
      );
    }
  };

  const engagementRows: EngagementRow[] = [
    {
      key: 'advisor',
      label: 'Advisor',
      staffField: 'agent_staff_id',
      appointDateField: 'agent_appoint_date',
      resignDateField: 'agent_resign_date',
      hasAttachment: false,
    },
    {
      key: 'tax',
      label: 'Tax Agent',
      staffField: 'staff_id',
      appointDateField: 'appoint_date',
      resignDateField: 'resign_date',
      attachmentField: 'tax',
      hasAttachment: true,
    },
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', width: '150px', borderRight: '1px solid #f0f0f0' }}></th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Staff</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Appoint Date</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Resign Date</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa' }}>Attachment</th>
          </tr>
        </thead>
        <tbody>
          {engagementRows.map((row) => (
            <tr key={row.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px', fontWeight: 600, backgroundColor: '#fafafa', width: '150px', borderRight: '1px solid #f0f0f0' }}>
                {row.label}
              </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
                {canEdit ? (
                  <Form.Item name={row.staffField} style={{ margin: 0 }}>
                    <Select allowClear placeholder="Select staff" style={{ width: '100%' }}>
                      {staff.map((staffItem) => (
                        <Select.Option key={staffItem.id} value={staffItem.id}>
                          {staffItem.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <div>
                    {(() => {
                      const staffId = form.getFieldValue(row.staffField);
                      const foundStaff = staff.find(s => s.id === staffId);
                      return foundStaff?.name || '-';
                    })()}
                  </div>
                )}
                {(() => {
                  const requestedStaffId = getRequestedValue(row.staffField);
                  const currentStaffId = form.getFieldValue(row.staffField);
                  if (requestedStaffId && requestedStaffId !== currentStaffId) {
                    const requestedStaff = staff.find(s => s.id === requestedStaffId);
                    return renderRequestedText(requestedStaff?.name || String(requestedStaffId));
                  }
                  return null;
                })()}
              </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
                {canEdit ? (
                  <Form.Item 
                    name={row.appointDateField} 
                    style={{ margin: 0 }}
                    dependencies={[row.resignDateField]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD/MM/YYYY"
                      onChange={(date) => {
                        // Ensure date is set to start of day to avoid timezone issues
                        if (date) {
                          form.setFieldValue(row.appointDateField, date.startOf('day'));
                        } else {
                          form.setFieldValue(row.appointDateField, null);
                        }
                        if (onEngagementDateChange) {
                          onEngagementDateChange();
                        }
                      }}
                    />
                  </Form.Item>
                ) : (
                  <div>
                    {(() => {
                      const appointDate = form.getFieldValue(row.appointDateField);
                      if (appointDate) {
                        return dayjs(appointDate).format('DD/MM/YYYY');
                      }
                      return '-';
                    })()}
                  </div>
                )}
                {(() => {
                  const requestedDate = getRequestedValue(row.appointDateField);
                  const currentDate = form.getFieldValue(row.appointDateField);
                  if (requestedDate && normalizeRequestedDate(requestedDate) !== normalizeRequestedDate(currentDate)) {
                    return renderRequestedText(dayjs(requestedDate).format('DD/MM/YYYY'));
                  }
                  return null;
                })()}
              </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
                {canEdit ? (
                  <Form.Item 
                    name={row.resignDateField} 
                    style={{ margin: 0 }}
                    dependencies={[row.appointDateField]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD/MM/YYYY"
                      onChange={(date) => {
                        // Ensure date is set to start of day to avoid timezone issues
                        if (date) {
                          form.setFieldValue(row.resignDateField, date.startOf('day'));
                        } else {
                          form.setFieldValue(row.resignDateField, null);
                        }
                        if (onEngagementDateChange) {
                          onEngagementDateChange();
                        }
                      }}
                    />
                  </Form.Item>
                ) : (
                  <div>
                    {(() => {
                      const resignDate = form.getFieldValue(row.resignDateField);
                      if (resignDate) {
                        return dayjs(resignDate).format('DD/MM/YYYY');
                      }
                      return '-';
                    })()}
                  </div>
                )}
                {(() => {
                  const requestedDate = getRequestedValue(row.resignDateField);
                  const currentDate = form.getFieldValue(row.resignDateField);
                  if (requestedDate && normalizeRequestedDate(requestedDate) !== normalizeRequestedDate(currentDate)) {
                    return renderRequestedText(dayjs(requestedDate).format('DD/MM/YYYY'));
                  }
                  return null;
                })()}
              </td>
              <td style={{ padding: '12px' }}>
                {row.hasAttachment ? (
                  <>
                    {renderAttachmentField(row.attachmentField!, reportingFiles[row.attachmentField as keyof typeof reportingFiles])}
                  </>
                ) : (
                  <div style={{ color: '#999', fontSize: '12px' }}>-</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
